import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ActivityStreamClient } from "./activity-stream-client";
import type { components } from "../generated/schema";
import { getSseConnectionId } from "./sse-connection";

type ActivityDto = components["schemas"]["ReturnRequestLogDto"];
type ReturnRequestDto = components["schemas"]["ReturnRequestDto"];

export interface UseActivityStreamParams {
  shopId: string;
  returnRequestId: string;
  enabled?: boolean;
  onNewActivity?: (activity: ActivityDto) => void;
  getAccessToken: () => string | null;
  onError?: (error: Error) => void;
  sseConnectionId?: string | null;
}

export function useActivityStream(params: UseActivityStreamParams) {
  const {
    shopId,
    returnRequestId,
    enabled = true,
    onNewActivity,
    getAccessToken,
    sseConnectionId,
  } = params;

  const queryClient = useQueryClient();
  const clientConnectionId = sseConnectionId ?? getSseConnectionId();

  return useQuery({
    queryKey: ["activity-stream", shopId, returnRequestId],
    queryFn: async ({ signal }) => {
      const queryKey = [
        "get",
        "/shops/{shopId}/return-requests/{returnRequestId}",
        {
          params: {
            path: {
              shopId,
              returnRequestId,
            },
          },
        },
      ];

      const client = new ActivityStreamClient({
        shopId,
        returnRequestId,
        getAccessToken,
        onActivity: (activity, eventConnectionId) => {
          queryClient.setQueryData(
            queryKey,
            (prev: ReturnRequestDto | undefined) => {
              if (!prev) return prev;

              const isDuplicate = prev.logs.some(
                (log) => log.id === activity.id,
              );
              if (isDuplicate) return prev;

              const isOwnEvent =
                eventConnectionId &&
                clientConnectionId &&
                eventConnectionId === clientConnectionId;

              if (!isOwnEvent) {
                onNewActivity?.(activity);
              }

              return {
                ...prev,
                logs: [...prev.logs, activity],
              };
            },
          );
        },
        sseConnectionId: clientConnectionId,
      });

      client.connect();

      // Return a promise that never resolves but cleans up on abort
      return new Promise<void>((_, reject) => {
        signal?.addEventListener("abort", () => {
          client.disconnect();
          reject(new Error("Query aborted"));
        });
      });
    },
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });
}
